import axios, {
  AxiosError,
  AxiosInterceptorManager,
  AxiosResponse,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from 'axios';

import { removeAuthToken } from '@/features/auth';

type RequestUseInterceptorsParams = Parameters<
  AxiosInterceptorManager<InternalAxiosRequestConfig>['use']
>;

type ResponseUseInterceptorsParams = Parameters<
  AxiosInterceptorManager<AxiosResponse>['use']
>;

const requestOnFulfilled: RequestUseInterceptorsParams[0] = (
  config: InternalAxiosRequestConfig,
) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

const requestOnRejected: RequestUseInterceptorsParams[1] = (
  error: AxiosError,
) => {
  return Promise.reject(error);
};

const responseOnFulfilled: ResponseUseInterceptorsParams[0] = (response) =>
  response;

const responseOnRejected: ResponseUseInterceptorsParams[1] = (
  error: AxiosError,
) => {
  if (error.response?.status === 401) {
    removeAuthToken();
    window.location.replace(ENV.HOME_URL);
  }

  return Promise.reject(error);
};

const baseAxiosConfig: CreateAxiosDefaults = {
  headers: {
    'Content-Type': 'application/json',
  },
};

const coreTrustAxiosClient = axios.create({
  ...baseAxiosConfig,
  baseURL: `${ENV.API_URL}/api`,
});

const coreActionsAxiosClient = axios.create({
  ...baseAxiosConfig,
  baseURL: `${ENV.ACTIONS_API_URL}/api`,
});

const authServiceAxiosClient = axios.create({
  ...baseAxiosConfig,
  baseURL: `${ENV.AUTH_SERVICE_URL}/api`,
});

const coreLettersAxiosClient = axios.create({
  ...baseAxiosConfig,
  baseURL: `${ENV.LETTERS_API_URL}/api`,
});

const cimAxiosClient = axios.create({
  ...baseAxiosConfig,
  baseURL: `${ENV.CIM_SERVICE_URL}/api`,
});

const fuseSignAxiosClient = axios.create({
  ...baseAxiosConfig,
  baseURL: `${ENV.FUSESIGN_API_URL}/api`,
});

const engageAxiosClient = axios.create({
  ...baseAxiosConfig,
  baseURL: `${ENV.ENGAGE_API_URL}/api`,
});

const goCardlessAxiosClient = axios.create({
  ...baseAxiosConfig,
  baseURL: `${ENV.PAYMENTS_API_URL}/api`,
});

// * Core Trust API
coreTrustAxiosClient.interceptors.request.use(
  requestOnFulfilled,
  requestOnRejected,
);

coreTrustAxiosClient.interceptors.response.use(
  responseOnFulfilled,
  responseOnRejected,
);

// * Letters API
coreLettersAxiosClient.interceptors.request.use(
  requestOnFulfilled,
  requestOnRejected,
);

coreLettersAxiosClient.interceptors.response.use(
  responseOnFulfilled,
  responseOnRejected,
);

// * Core Actions API
coreActionsAxiosClient.interceptors.request.use(
  requestOnFulfilled,
  requestOnRejected,
);

coreActionsAxiosClient.interceptors.response.use(
  responseOnFulfilled,
  responseOnRejected,
);

// * Auth Service API
authServiceAxiosClient.interceptors.request.use(
  requestOnFulfilled,
  requestOnRejected,
);

authServiceAxiosClient.interceptors.response.use(
  responseOnFulfilled,
  responseOnRejected,
);

// * CIM Service API
cimAxiosClient.interceptors.request.use(requestOnFulfilled, requestOnRejected);

cimAxiosClient.interceptors.response.use(
  responseOnFulfilled,
  responseOnRejected,
);

// * FuseSign Service API
fuseSignAxiosClient.interceptors.request.use(
  requestOnFulfilled,
  requestOnRejected,
);

fuseSignAxiosClient.interceptors.response.use(
  responseOnFulfilled,
  responseOnRejected,
);

// * Engage API
engageAxiosClient.interceptors.request.use(
  requestOnFulfilled,
  requestOnRejected,
);

engageAxiosClient.interceptors.response.use(
  responseOnFulfilled,
  responseOnRejected,
);

// * GoCardless API
goCardlessAxiosClient.interceptors.request.use(
  requestOnFulfilled,
  requestOnRejected,
);

goCardlessAxiosClient.interceptors.response.use(
  responseOnFulfilled,
  responseOnRejected,
);

export {
  authServiceAxiosClient,
  cimAxiosClient,
  coreActionsAxiosClient,
  coreLettersAxiosClient,
  coreTrustAxiosClient,
  engageAxiosClient,
  fuseSignAxiosClient,
  goCardlessAxiosClient,
};
